/**
 * Pilotage-plan chart interactions — the bridge between a chart tap/drag and the
 * planner store, active only while building a plan. Kept out of MapView's big tap
 * pipeline: MapView just guards its normal handlers when building and attaches
 * these instead, so plan editing is self-contained.
 *
 * A tap does what the armed tool says (drop a route waypoint, place a note, set a
 * wheel-over/transit/clearing/back bearing, or work a depth contour). Waypoints
 * drag after placement, and the freehand tool draws while the finger moves. Snap
 * to a charted object (a buoy, light or beacon) uses the live cell layers, so a
 * bearing anchors on the real object under the tap.
 */
import type { Map as MaplibreMap, MapMouseEvent, MapTouchEvent } from 'maplibre-gl';
import { rhumbBearingDeg, rhumbDistanceNm, rhumbDestination } from '@/nav';
import { sideOfLine } from '@/pilotage/planGeometry';
import { sliceContourBetween } from '@/pilotage/plannerTools';
import { usePilotagePlannerStore, selectActivePlan } from '@/store/pilotagePlanner';
import { useShellStore } from '@/store/shell';
import type { LonLat } from '@/pilotage/plannerModel';

/** The cell (vector chart) layer ids, for querying charted objects under a tap. */
function cellLayerIds(map: MaplibreMap): string[] {
  return map.getStyle().layers?.map((l) => l.id).filter((id) => id.startsWith('cell-')) ?? [];
}

/** Read a feature's decoded attrs whether MapLibre kept them nested or stringified. */
function attrsOf(props: Record<string, unknown> | null): Record<string, unknown> {
  if (!props) return {};
  const a = props.attrs;
  if (typeof a === 'string') {
    try {
      return JSON.parse(a) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return (a as Record<string, unknown>) ?? {};
}

export interface SnapResult {
  position: LonLat;
  label: string;
  snapped: boolean;
}

/** Snap to the nearest charted point object (buoy/light/beacon) within a pad, else the raw tap. */
function snapCharted(map: MaplibreMap, point: { x: number; y: number }, lnglat: LonLat): SnapResult {
  const ids = cellLayerIds(map);
  if (!ids.length) return { position: lnglat, label: '', snapped: false };
  const pad = 12;
  const feats = map.queryRenderedFeatures(
    [
      [point.x - pad, point.y - pad],
      [point.x + pad, point.y + pad],
    ],
    { layers: ids },
  );
  for (const f of feats) {
    if (f.geometry.type === 'Point') {
      const attrs = attrsOf(f.properties as Record<string, unknown>);
      const name = typeof attrs.name === 'string' ? attrs.name : undefined;
      const objClass = typeof f.properties?.objClass === 'string' ? f.properties.objClass : undefined;
      const c = f.geometry.coordinates as [number, number];
      return { position: [c[0], c[1]], label: name || objClass || 'object', snapped: true };
    }
  }
  const objClass = feats[0]?.properties?.objClass;
  return { position: lnglat, label: typeof objClass === 'string' ? objClass : '', snapped: false };
}

/** The depth contour under a tap (its path + charted depth value), or null. */
function contourAt(map: MaplibreMap, point: { x: number; y: number }, lnglat: LonLat): { path: LonLat[]; depthM?: number } | null {
  const ids = cellLayerIds(map);
  if (!ids.length) return null;
  const pad = 16; // generous — contours are thin lines on a phone
  const feats = map.queryRenderedFeatures(
    [
      [point.x - pad, point.y - pad],
      [point.x + pad, point.y + pad],
    ],
    { layers: ids },
  );
  for (const f of feats) {
    const objClass = f.properties?.objClass;
    if (objClass !== 'DEPCNT' && objClass !== 'DEPARE') continue;
    const attrs = attrsOf(f.properties as Record<string, unknown>);
    // Decoded ENC depth for DEPCNT/DEPARE lives in attrs.depthM (= VALDCO after
    // decoding) — the same key the clearing-depth layer and the feature sheet
    // read. It can arrive as a numeric string in the tile, so coerce it. The raw
    // VALDCO/valdco keys only exist on undecoded features, so keep them as a
    // fallback but read depthM first; reading VALDCO alone stored depthM as
    // undefined on every contour-follow / clearing-depth pick.
    const rawDepth = attrs.depthM ?? attrs.VALDCO ?? attrs.valdco;
    const depthM =
      typeof rawDepth === 'number'
        ? rawDepth
        : typeof rawDepth === 'string' && rawDepth.trim() !== '' && Number.isFinite(Number(rawDepth))
          ? Number(rawDepth)
          : undefined;
    if (f.geometry.type === 'LineString') {
      return { path: (f.geometry.coordinates as [number, number][]).map((c) => [c[0], c[1]] as LonLat), depthM };
    }
    if (f.geometry.type === 'MultiLineString') {
      // Pick the sub-line whose nearest vertex is closest to the tap.
      let best: LonLat[] | null = null;
      let bestD = Infinity;
      for (const seg of f.geometry.coordinates as [number, number][][]) {
        for (const c of seg) {
          const d = rhumbDistanceNm([c[0], c[1]], lnglat);
          if (d < bestD) {
            bestD = d;
            best = seg.map((p) => [p[0], p[1]] as LonLat);
          }
        }
      }
      if (best) return { path: best, depthM };
    }
  }
  return null;
}

/** Handle one chart tap while building a plan. */
export function handlePlanTap(map: MaplibreMap, point: { x: number; y: number }, lnglat: LonLat): void {
  const st = usePilotagePlannerStore.getState();
  if (!st.building || !st.activePlanId) return;
  const plan = selectActivePlan(st);
  if (!plan) return;
  const tool = st.activeTool;

  switch (tool) {
    case 'route':
      st.addRouteWaypoint(lnglat);
      return;

    case 'note': {
      const id = st.addStickyNote(lnglat, '');
      st.setOpenNote(id);
      return;
    }

    case 'wheelover': {
      if (!st.selectedWaypointId) {
        // Pick the nearest route waypoint to trigger the turn at.
        let nearest: string | null = null;
        let bestD = Infinity;
        for (const w of plan.route.waypoints) {
          const d = rhumbDistanceNm(w.position, lnglat);
          if (d < bestD) {
            bestD = d;
            nearest = w.id;
          }
        }
        if (!nearest) {
          st.setOpenNote(null);
          planToast('Lay a route first, then tap a waypoint for the wheel-over');
          return;
        }
        st.selectWaypoint(nearest);
        planToast('Now tap the landmark the turn triggers off');
        return;
      }
      const wp = plan.route.waypoints.find((w) => w.id === st.selectedWaypointId);
      if (wp) {
        const snap = snapCharted(map, point, lnglat);
        st.addWheelOver(wp.id, snap.position, rhumbBearingDeg(wp.position, snap.position), snap.label || undefined);
        planToast(snap.snapped ? `Wheel-over on ${snap.label}` : 'Wheel-over set');
      }
      st.selectWaypoint(null);
      return;
    }

    case 'transit': {
      const snap = snapCharted(map, point, lnglat);
      if (!st.pendingTransitBack) {
        st.setPendingTransitBack({ position: snap.position, label: snap.label || undefined });
        planToast(snap.snapped ? `Rear mark on ${snap.label} — now the front mark` : 'Rear mark set — now the front mark');
        return;
      }
      st.addTransit(snap.position, st.pendingTransitBack.position, snap.label || undefined, st.pendingTransitBack.label);
      st.setPendingTransitBack(null);
      planToast('Transit set');
      return;
    }

    case 'clearing': {
      if (!st.pendingTransitBack) {
        const snap = snapCharted(map, point, lnglat);
        st.setPendingTransitBack({ position: snap.position, label: snap.label || undefined });
        planToast(snap.snapped ? `Anchored on ${snap.label} — tap the safe side` : 'Anchored — tap the safe side');
        return;
      }
      const anchor = st.pendingTransitBack.position;
      const bearingDeg = rhumbBearingDeg(lnglat, anchor); // object bears this FROM the vessel
      const lengthNm = Math.max(0.05, rhumbDistanceNm(anchor, lnglat));
      const farEnd = rhumbDestination(anchor, bearingDeg, 0.3);
      const safeSide = sideOfLine(anchor, farEnd, lnglat) === 'left' ? 'left' : 'right';
      st.addClearingBearing({ anchor, bearingDeg, lengthNm, safeSide, anchorLabel: st.pendingTransitBack.label });
      st.setPendingTransitBack(null);
      planToast('Clearing bearing set');
      return;
    }

    case 'back': {
      if (!st.pendingTransitBack) {
        const snap = snapCharted(map, point, lnglat);
        st.setPendingTransitBack({ position: snap.position, label: snap.label || undefined });
        planToast(snap.snapped ? `Object ${snap.label} — tap the vessel side` : 'Object set — tap the vessel side');
        return;
      }
      const anchor = st.pendingTransitBack.position;
      const bearingDeg = rhumbBearingDeg(lnglat, anchor);
      const lengthNm = Math.max(0.05, rhumbDistanceNm(anchor, lnglat));
      st.addBackBearing({ anchor, bearingDeg, lengthNm, kind: 'back', anchorLabel: st.pendingTransitBack.label });
      st.setPendingTransitBack(null);
      planToast('Back bearing set');
      return;
    }

    case 'contour': {
      const pc = st.pendingContour;
      if (!pc) {
        const c = contourAt(map, point, lnglat);
        if (!c) {
          planToast('No contour there — tap right on the depth line');
          return;
        }
        st.setPendingContour({ path: c.path, depthM: c.depthM, following: false });
        planToast('Contour selected — choose Follow or Clearing depth in the toolbar');
        return;
      }
      if (pc.following) {
        if (!pc.followFrom) {
          st.setPendingContour({ ...pc, followFrom: lnglat });
          planToast('Start set — tap the far end along the contour');
          return;
        }
        const seg = sliceContourBetween(pc.path, pc.followFrom, lnglat);
        st.addContourFollow(seg, pc.depthM);
        st.setPendingContour(null);
        planToast('Route follows the contour here');
      }
      return;
    }

    default:
      // No tool (or freehand, which is handled by the drag path): a tap selects a
      // waypoint or opens a note if one is under the finger.
      return;
  }
}

/** Fire a shell toast. */
function planToast(msg: string): void {
  useShellStore.getState().showToast(msg);
}

/** A plan waypoint under a screen point (its id), or null. */
function planWaypointAtMap(map: MaplibreMap, point: { x: number; y: number }): string | null {
  if (!map.getLayer('plan-waypoint')) return null;
  const pad = 12;
  const hits = map.queryRenderedFeatures(
    [
      [point.x - pad, point.y - pad],
      [point.x + pad, point.y + pad],
    ],
    { layers: ['plan-waypoint'] },
  );
  const id = hits[0]?.properties?.id;
  return typeof id === 'string' ? id : null;
}

/** A sticky note under a screen point (its id), or null. */
function planNoteAtMap(map: MaplibreMap, point: { x: number; y: number }): string | null {
  if (!map.getLayer('plan-note')) return null;
  const pad = 12;
  const hits = map.queryRenderedFeatures(
    [
      [point.x - pad, point.y - pad],
      [point.x + pad, point.y + pad],
    ],
    { layers: ['plan-note'] },
  );
  const id = hits[0]?.properties?.id;
  return typeof id === 'string' ? id : null;
}

/** Set true by a drag/freehand gesture so the click it produces is swallowed. */
let swallowNextClick = false;

/**
 * Handle a chart CLICK while building a plan. Called from MapView's own click
 * handler (which is guaranteed to fire), so plan taps never depend on a second
 * listener being attached. With no tool armed a tap selects a waypoint or opens a
 * note; otherwise it runs the armed tool.
 */
export function handlePlanClick(map: MaplibreMap, point: { x: number; y: number }, lnglat: LonLat): void {
  const st = usePilotagePlannerStore.getState();
  if (!st.building || !st.activePlanId) return;
  if (swallowNextClick) {
    swallowNextClick = false;
    return;
  }
  // A note tap always opens it (unless we're placing a fresh note).
  const noteId = planNoteAtMap(map, point);
  if (noteId && st.activeTool !== 'note') {
    st.setOpenNote(noteId);
    return;
  }
  if (!st.activeTool) {
    const wpId = planWaypointAtMap(map, point);
    if (wpId) {
      st.selectWaypoint(st.selectedWaypointId === wpId ? null : wpId);
      return;
    }
    return;
  }
  handlePlanTap(map, point, lnglat);
}

interface Gesture {
  kind: 'waypoint' | 'freehand';
  wpId?: string;
  points?: LonLat[];
  moved: boolean;
}

/**
 * Attach the plan-building DRAG and FREEHAND handlers (waypoint dragging and
 * finger sketching). Tapping is handled by MapView's click handler via
 * handlePlanClick, so this only deals with press-and-move gestures. Returns a
 * detach fn.
 */
export function attachPlanInteractions(map: MaplibreMap): () => void {
  let gesture: Gesture | null = null;

  const onDown = (e: MapMouseEvent | MapTouchEvent) => {
    const st = usePilotagePlannerStore.getState();
    if (!st.building) return;
    const wpId = planWaypointAtMap(map, e.point);
    // The wheel-over tool's first tap is a waypoint PICK (the point the turn is
    // triggered at), so a press on a waypoint must fall through to the click
    // handler that selects it. If we armed a waypoint-drag here instead, the
    // slightest finger movement would move the waypoint and set swallowNextClick,
    // eating the selecting tap — so the wheel-over never got past step one and
    // "didn't seem to do anything" (fb-ms1nqzrm-5). Repositioning a waypoint by
    // dragging stays available with the route tool (or no tool armed).
    if (wpId && st.activeTool !== 'wheelover') {
      gesture = { kind: 'waypoint', wpId, moved: false };
      e.preventDefault();
      map.dragPan.disable();
      return;
    }
    if (st.activeTool === 'freehand') {
      gesture = { kind: 'freehand', points: [[e.lngLat.lng, e.lngLat.lat]], moved: false };
      e.preventDefault();
      map.dragPan.disable();
    }
  };
  const onMove = (e: MapMouseEvent | MapTouchEvent) => {
    if (!gesture) return;
    gesture.moved = true;
    const ll: LonLat = [e.lngLat.lng, e.lngLat.lat];
    if (gesture.kind === 'waypoint' && gesture.wpId) {
      usePilotagePlannerStore.getState().moveRouteWaypoint(gesture.wpId, ll);
    } else if (gesture.kind === 'freehand' && gesture.points) {
      gesture.points.push(ll);
    }
  };
  const onUp = () => {
    if (!gesture) return;
    if (gesture.kind === 'freehand' && gesture.points && gesture.points.length >= 2) {
      usePilotagePlannerStore.getState().addFreehand('line', gesture.points);
    }
    // Swallow the click this gesture will produce, so a drag/sketch doesn't also
    // drop a waypoint.
    if (gesture.moved) swallowNextClick = true;
    gesture = null;
    map.dragPan.enable();
  };

  map.on('mousedown', onDown);
  map.on('touchstart', onDown);
  map.on('mousemove', onMove);
  map.on('touchmove', onMove);
  map.on('mouseup', onUp);
  map.on('touchend', onUp);
  return () => {
    map.off('mousedown', onDown);
    map.off('touchstart', onDown);
    map.off('mousemove', onMove);
    map.off('touchmove', onMove);
    map.off('mouseup', onUp);
    map.off('touchend', onUp);
    map.dragPan.enable();
  };
}
